import { api } from '../../../lib/api';
import type { ListQuery, PagedResult } from '../../administration/users/userTypes';
import type { IdentificationType, IdentificationTypeRequest } from './identificationTypes';

function toQueryString(query: ListQuery): string {
  const params = new URLSearchParams();

  if (query.search?.trim()) params.set('search', query.search.trim());
  if (query.page && query.page > 1) params.set('page', String(query.page));
  if (query.pageSize) params.set('pageSize', String(query.pageSize));
  if (query.sort) params.set('sort', query.sort);

  /*
    Sent whenever the key is present, empty string included — an empty value is the reader
    saying "none of these", which matches no row. A truthiness check would drop it and the
    server would read the axis as unmentioned.
  */
  if (query.status !== undefined) params.set('status', query.status);

  const serialized = params.toString();
  return serialized ? `?${serialized}` : '';
}

export const identificationApi = {
  /** Soft-deletes every type at once; resolves with how many. Numbers already held are untouched. */
  clear: () => api<number>('/api/identification-types/clear', { method: 'POST' }),

  list: (query: ListQuery, signal?: AbortSignal) =>
    api<PagedResult<IdentificationType>>(`/api/identification-types${toQueryString(query)}`, { signal }),

  get: (id: string, signal?: AbortSignal) =>
    api<IdentificationType>(`/api/identification-types/${id}`, { signal }),

  /**
   * The ACTIVE types, in order, for the personnel form's Identification step.
   *
   * Deleted types are left out: the form offers what can be collected now. A record already
   * holding a number of a deleted kind keeps it and still names the type on its profile.
   */
  options: (signal?: AbortSignal) =>
    api<IdentificationType[]>('/api/identification-types/options', { signal }),

  create: (request: IdentificationTypeRequest) =>
    api<IdentificationType>('/api/identification-types', { method: 'POST', body: request }),

  update: (id: string, request: IdentificationTypeRequest) =>
    api<IdentificationType>(`/api/identification-types/${id}`, { method: 'PUT', body: request }),

  /** Soft delete: the field leaves the form, the numbers stay on the records. */
  deactivate: (id: string) => api<void>(`/api/identification-types/${id}`, { method: 'DELETE' }),

  activate: (id: string) => api<void>(`/api/identification-types/${id}/restore`, { method: 'POST' }),
};
